import React, { ReactNode, ErrorInfo } from 'react';

// ─── Type definitions ────────────────────────────────────────────────

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
	message?: string;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
}

interface SectionErrorBoundaryProps {
	children: ReactNode;
	section?: string;
}

interface AppErrorBoundaryProps {
	children: ReactNode;
}

// Brand colors (match Skulicity theme)
const brandColors = {
	navy: '#1a2b6b',
	pink: '#ec008c',
} as const;

// ─── Inline styles (no Tailwind dependency) ──────────────────────────

const styles = {
	container: {
		minHeight: 300,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 32,
	} as React.CSSProperties,
	inner: {
		maxWidth: 440,
		width: '100%',
		textAlign: 'center' as const,
	} as React.CSSProperties,
	iconWrapper: {
		width: 64,
		height: 64,
		margin: '0 auto 24px',
		borderRadius: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: brandColors.pink + '15',
	} as React.CSSProperties,
	heading: {
		fontSize: 20,
		fontWeight: 700,
		marginBottom: 8,
		color: brandColors.navy,
	} as React.CSSProperties,
	message: {
		color: '#64748b',
		marginBottom: 24,
		fontSize: 14,
	} as React.CSSProperties,
	detailsSummary: {
		cursor: 'pointer',
		fontSize: 12,
		color: '#94a3b8',
		marginBottom: 8,
	} as React.CSSProperties,
	detailsBox: {
		marginBottom: 24,
		padding: 12,
		background: '#f1f5f9',
		borderRadius: 8,
		fontSize: 11,
		fontFamily: 'monospace',
		color: '#475569',
		overflow: 'auto',
		maxHeight: 128,
		textAlign: 'left' as const,
	} as React.CSSProperties,
	buttonRow: {
		display: 'flex',
		gap: 12,
		justifyContent: 'center',
	} as React.CSSProperties,
	primaryBtn: {
		padding: '8px 16px',
		borderRadius: 8,
		fontWeight: 500,
		color: '#fff',
		backgroundColor: brandColors.pink,
		border: 'none',
		cursor: 'pointer',
		fontSize: 14,
	} as React.CSSProperties,
	secondaryBtn: {
		padding: '8px 16px',
		borderRadius: 8,
		fontWeight: 500,
		color: '#475569',
		backgroundColor: '#fff',
		border: '1px solid #e2e8f0',
		cursor: 'pointer',
		fontSize: 14,
	} as React.CSSProperties,
	helpText: {
		marginTop: 24,
		fontSize: 11,
		color: '#94a3b8',
	} as React.CSSProperties,
} as const;

// ─── Error Boundary Class Component ──────────────────────────────────

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null, errorInfo: null };
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		this.setState({ errorInfo });
		console.error('[ErrorBoundary]', error, errorInfo);
	}

	handleReset = () => {
		this.setState({ hasError: false, error: null, errorInfo: null });
	};

	handleReload = () => {
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) return this.props.fallback;

			return (
				<div style={styles.container}>
					<div style={styles.inner}>
						{/* Error icon */}
						<div style={styles.iconWrapper}>
							<svg width={32} height={32} fill="none" stroke={brandColors.pink} viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
								/>
							</svg>
						</div>

						<h2 style={styles.heading}>Something went wrong</h2>
						<p style={styles.message}>
							{this.props.message || 'We encountered an unexpected error. Please try again.'}
						</p>

						{/* Dev-only error details */}
						{process.env.NODE_ENV === 'development' && this.state.error && (
							<details style={{ marginBottom: 24 }}>
								<summary style={styles.detailsSummary}>Technical Details</summary>
								<div style={styles.detailsBox}>
									<div style={{ fontWeight: 600, color: '#dc2626', marginBottom: 4 }}>
										{this.state.error.toString()}
									</div>
									{this.state.errorInfo?.componentStack && (
										<pre style={{ whiteSpace: 'pre-wrap', color: '#64748b', margin: 0 }}>
											{this.state.errorInfo.componentStack}
										</pre>
									)}
								</div>
							</details>
						)}

						<div style={styles.buttonRow}>
							<button onClick={this.handleReset} style={styles.primaryBtn}>
								Try Again
							</button>
							<button onClick={this.handleReload} style={styles.secondaryBtn}>
								Reload Page
							</button>
						</div>

						<p style={styles.helpText}>If the problem persists, please contact support.</p>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

// ─── Convenience wrappers ────────────────────────────────────────────

/** Wraps a specific section — shows inline error with retry */
export const SectionErrorBoundary: React.FC<SectionErrorBoundaryProps> = ({ children, section = 'section' }) => (
	<ErrorBoundary message={`There was a problem loading this ${section}. Please try refreshing.`}>
		{children}
	</ErrorBoundary>
);

/** Wraps the entire app — shows full-page error with reload */
export const AppErrorBoundary: React.FC<AppErrorBoundaryProps> = ({ children }) => (
	<ErrorBoundary message="The application encountered an error. Please reload the page.">{children}</ErrorBoundary>
);

export default ErrorBoundary;
